import React, { useState, useEffect } from 'react';
import { LearningProgress, StudyStats } from '../types/learning';
import { learningService } from '../services/learningService';

interface LearningResumeProps {
  onContinue: (progress: LearningProgress) => void;
  onNewStart: () => void;
}

const LearningResume: React.FC<LearningResumeProps> = ({ onContinue, onNewStart }) => {
  const [progress, setProgress] = useState<LearningProgress | null>(null);
  const [stats, setStats] = useState<StudyStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      const [progressData, statsData] = await Promise.all([
        learningService.getProgress(),
        learningService.getStats()
      ]);
      setProgress(progressData);
      setStats(statsData);
      setLoading(false);
    };
    loadData();
  }, []);

  const formatTime = (minutes: number) => {
    if (minutes < 60) return `${minutes}分钟`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}小时${mins > 0 ? mins + '分钟' : ''}`;
  };

  const getTimeAgo = (dateString: string) => {
    const diff = Date.now() - new Date(dateString).getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    if (hours < 1) return '刚刚';
    if (hours < 24) return `${hours}小时前`;
    const days = Math.floor(hours / 24);
    return `${days}天前`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg">加载学习记录中...</div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center min-h-screen p-4">
      <div className="max-w-md w-full space-y-4">
        {progress && (
          <div className="bg-white rounded-lg p-6 shadow-lg">
            <h2 className="text-xl font-bold mb-4 flex items-center">
              📖 继续上次学习
            </h2>
            <div className="space-y-3 mb-4">
              <div className="text-lg font-medium">{progress.category}</div>
              <div className="text-sm text-gray-600">
                第{progress.subCategoryIndex + 1}组 · 音频 {progress.audioIndex + 1}
              </div>
              <div className="text-sm text-gray-500">
                进度: {Math.round(((progress.subCategoryIndex * 20 + progress.audioIndex) / progress.totalAudiosInCategory) * 100)}%
              </div>
              <div className="text-xs text-gray-400">
                上次学习: {getTimeAgo(progress.lastStudyTime)}
              </div>
            </div>
            <button
              onClick={() => onContinue(progress)}
              className="w-full bg-blue-500 text-white py-3 px-4 rounded-lg hover:bg-blue-600 transition-colors"
            >
              继续学习
            </button>
          </div>
        )}

        <div className="bg-white rounded-lg p-6 shadow-lg">
          <button
            onClick={onNewStart}
            className="w-full bg-green-500 text-white py-3 px-4 rounded-lg hover:bg-green-600 transition-colors"
          >
            🆕 开始新的学习
          </button>
        </div>

        {stats && (
          <div className="bg-white rounded-lg p-6 shadow-lg">
            <h3 className="text-lg font-bold mb-3">📊 学习统计</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div className="text-center">
                <div className="font-bold text-lg text-blue-600">{stats.todayAudios}</div>
                <div className="text-gray-500">今日音频</div>
              </div>
              <div className="text-center">
                <div className="font-bold text-lg text-blue-600">{formatTime(stats.todayMinutes)}</div>
                <div className="text-gray-500">今日时长</div>
              </div>
              <div className="text-center">
                <div className="font-bold text-lg text-green-600">{stats.weekAudios}</div>
                <div className="text-gray-500">本周音频</div>
              </div>
              <div className="text-center">
                <div className="font-bold text-lg text-green-600">{formatTime(stats.weekMinutes)}</div>
                <div className="text-gray-500">本周时长</div>
              </div>
            </div>
            <div className="mt-4 pt-4 border-t text-center">
              <div className="text-sm text-gray-600">
                总计: {stats.totalAudios}个音频 · {formatTime(stats.totalMinutes)}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LearningResume;
